import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OverlayModal from '../ui/OverlayModal';
import WizardSelect from '../ui/WizardSelect';
import {
  getCategoryAllocationUsers,
  getCategoryAllocations,
  type CaseRequestCategoryAllocationRow,
  type CategoryAllocationUserRow,
} from '../../services/caseRequestCategoryAllocation';
import {
  filterAssignableCounselUsers,
  formatCategoryAllocationUserLabel,
} from '../../utils/configureAssignmentFields';
import { fileCategoryExemptFromSubFileCategory, ensureExemptSubFileCategoryIdsLoaded, normalizeFileCategoryLabel } from '../../utils/subFileCategoryRules';

type LoadState = 'idle' | 'loading' | 'error';

export type ConfigureAssignmentSaveDetails = {
  supervisorId?: string;
  stateAttorneyId?: string;
  caseRequestCategoryAllocationUserId: string;
  userId: string;
};

export type ConfigureAssignmentModalProps = {
  isOpen: boolean;
  idPrefix: string;
  fileCategoryId: string;
  subFileCategoryId: string;
  fileCategoryName: string;
  subFileCategoryName: string;
  saving: boolean;
  workflowInstanceStageUserTaskId?: string;
  onClose: () => void;
  onCancel: () => void;
  onSave: (details: ConfigureAssignmentSaveDetails) => Promise<void>;
};

export default function ConfigureAssignmentModal({
  isOpen,
  idPrefix,
  fileCategoryId,
  subFileCategoryId,
  fileCategoryName,
  subFileCategoryName,
  saving,
  workflowInstanceStageUserTaskId,
  onClose,
  onCancel,
  onSave,
}: ConfigureAssignmentModalProps) {
  const { t } = useTranslation();
  const usersLoadRef = useRef<AbortController | null>(null);

  const [allocationsState, setAllocationsState] = useState<LoadState>('idle');
  const [allocations, setAllocations] = useState<CaseRequestCategoryAllocationRow[]>([]);
  const [selectedAllocationId, setSelectedAllocationId] = useState('');
  const [selectedSupervisorId, setSelectedSupervisorId] = useState('');

  const [usersState, setUsersState] = useState<LoadState>('idle');
  const [allocationUsers, setAllocationUsers] = useState<CategoryAllocationUserRow[]>([]);
  const [selectedStateAttorneyId, setSelectedStateAttorneyId] = useState('');
  const [selectedCounselId, setSelectedCounselId] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  const supervisorOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { supervisorId: string; label: string; defaultAllocationId: string }[] = [];
    for (const a of allocations) {
      if (seen.has(a.supervisorId)) continue;
      seen.add(a.supervisorId);
      const label = a.supervisorDisplayName?.trim() || a.supervisorId;
      out.push({ supervisorId: a.supervisorId, label, defaultAllocationId: a.id });
    }
    return out;
  }, [allocations]);

  const supervisorSelectOptions = useMemo(
    () => supervisorOptions.map((o) => ({ id: o.supervisorId, label: o.label })),
    [supervisorOptions],
  );

  const stateAttorneyOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { stateAttorneyId: string; label: string; defaultAllocationId: string }[] = [];
    for (const a of allocations) {
      if (!a.stateAttorneyId || seen.has(a.stateAttorneyId)) continue;
      seen.add(a.stateAttorneyId);
      const label = a.stateAttorneyDisplayName?.trim() || a.stateAttorneyId;
      out.push({ stateAttorneyId: a.stateAttorneyId, label, defaultAllocationId: a.id });
    }
    return out;
  }, [allocations]);

  const stateAttorneySelectOptions = useMemo(
    () => stateAttorneyOptions.map((o) => ({ id: o.stateAttorneyId, label: o.label })),
    [stateAttorneyOptions],
  );

  const counselUsers = useMemo(
    () => filterAssignableCounselUsers(allocationUsers),
    [allocationUsers],
  );

  const counselSelectOptions = useMemo(
    () =>
      counselUsers.map((u) => ({
        id: u.caseRequestCategoryAllocationUserId,
        label: formatCategoryAllocationUserLabel(u),
      })),
    [counselUsers],
  );

  const usersReady = allocations.length > 0 && usersState !== 'loading';

  const supervisorPlaceholder =
    allocationsState === 'error' ? t('common.failedToLoad') : t('admin.userCategories.selectSupervisor');
  const stateAttorneyPlaceholder =
    allocationsState === 'error' ? t('common.failedToLoad') : t('admin.userCategories.selectStateAttorney');
  const counselPlaceholder = usersState === 'error' ? t('common.failedToLoad') : t('tasks.assign.counsel');

  const fetchAllocationUsers = useCallback(
    async (allocationIds: string[], signal: AbortSignal) => {
      if (!allocationIds.length) {
        setUsersState('idle');
        setAllocationUsers([]);
        return;
      }

      setUsersState('loading');
      setAllocationUsers([]);
      setSelectedCounselId('');
      setErrors((e) => ({ ...e, selectedCounselId: '' }));

      const results = await Promise.all(
        allocationIds.map((id) =>
          getCategoryAllocationUsers({ requestCategoryAllocationId: id, signal }),
        ),
      );
      if (signal.aborted) return;

      const merged: CategoryAllocationUserRow[] = [];
      let failed = false;
      for (let i = 0; i < allocationIds.length; i++) {
        const res = results[i];
        if (!res.success) {
          failed = true;
          continue;
        }
        for (const u of res.data) {
          merged.push({ ...u, requestCategoryAllocationId: allocationIds[i] });
        }
      }

      if (failed && merged.length === 0) {
        setUsersState('error');
        return;
      }

      setUsersState('idle');
      setAllocationUsers(merged);
    },
    [],
  );

  const resolveAllocationId = useCallback(
    (supervisorId: string, stateAttorneyId: string): string => {
      if (supervisorId && stateAttorneyId) {
        const match = allocations.find(
          (a) => a.supervisorId === supervisorId && a.stateAttorneyId === stateAttorneyId,
        );
        if (match) return match.id;
      }
      if (supervisorId) {
        return allocations.find((a) => a.supervisorId === supervisorId)?.id ?? '';
      }
      if (stateAttorneyId) {
        return allocations.find((a) => a.stateAttorneyId === stateAttorneyId)?.id ?? '';
      }
      return '';
    },
    [allocations],
  );

  const loadAllocationUsers = useCallback(
    (allocationId: string | string[]) => {
      const allocationIds = (Array.isArray(allocationId) ? allocationId : [allocationId]).filter(Boolean);
      if (!allocationIds.length) {
        usersLoadRef.current?.abort();
        usersLoadRef.current = null;
        setSelectedAllocationId('');
        setUsersState('idle');
        setAllocationUsers([]);
        return;
      }

      setSelectedAllocationId(allocationIds.length === 1 ? allocationIds[0] : '');
      usersLoadRef.current?.abort();
      const usersController = new AbortController();
      usersLoadRef.current = usersController;
      void fetchAllocationUsers(allocationIds, usersController.signal);
    },
    [fetchAllocationUsers],
  );

  const reloadAllocationUsers = useCallback(
    (supervisorId: string, stateAttorneyId: string) => {
      const allocationId = resolveAllocationId(supervisorId, stateAttorneyId);
      if (allocationId) {
        loadAllocationUsers(allocationId);
        return;
      }
      if (allocations.length > 0) {
        loadAllocationUsers(allocations.map((a) => a.id));
        return;
      }
      loadAllocationUsers('');
    },
    [allocations, loadAllocationUsers, resolveAllocationId],
  );

  useEffect(() => {
    const controller = new AbortController();
    const categoryId = fileCategoryId.trim();
    const subCategoryId = subFileCategoryId.trim();
    const categoryName = normalizeFileCategoryLabel(fileCategoryName);

    const load = async () => {
      await ensureExemptSubFileCategoryIdsLoaded();
      if (controller.signal.aborted) return;

      const exemptFromSubCategory = fileCategoryExemptFromSubFileCategory({
        id: categoryId,
        name: categoryName,
      });

      if (!categoryId || (!subCategoryId && !exemptFromSubCategory)) {
        setAllocationsState('idle');
        setUsersState('idle');
        setAllocations([]);
        setSelectedAllocationId('');
        setSelectedSupervisorId('');
        setAllocationUsers([]);
        setSelectedStateAttorneyId('');
        setSelectedCounselId('');
        return;
      }

      setAllocationsState('loading');
      setUsersState('idle');
      setAllocations([]);
      setSelectedAllocationId('');
      setSelectedSupervisorId('');
      setAllocationUsers([]);
      setSelectedStateAttorneyId('');
      setSelectedCounselId('');
      setErrors({});

      const res = await getCategoryAllocations({
        fileCategoryId: categoryId,
        subFileCategoryId: subCategoryId,
        fileCategoryName: categoryName,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (!res.success) {
        setAllocationsState('error');
        return;
      }
      setAllocationsState('idle');
      setAllocations(res.data);

      if (res.data.length === 1) {
        const only = res.data[0];
        setSelectedSupervisorId(only.supervisorId);
        setSelectedStateAttorneyId(only.stateAttorneyId);
        usersLoadRef.current?.abort();
        const usersController = new AbortController();
        usersLoadRef.current = usersController;
        await fetchAllocationUsers([only.id], usersController.signal);
        setSelectedAllocationId(only.id);
      } else if (res.data.length > 1) {
        usersLoadRef.current?.abort();
        const usersController = new AbortController();
        usersLoadRef.current = usersController;
        await fetchAllocationUsers(
          res.data.map((a) => a.id),
          usersController.signal,
        );
      }
    };

    void load();
    return () => {
      controller.abort();
      usersLoadRef.current?.abort();
    };
  }, [fileCategoryId, subFileCategoryId, fileCategoryName, fetchAllocationUsers]);

  const handleSupervisorChange = useCallback(
    (supervisorId: string) => {
      setSelectedSupervisorId(supervisorId);
      setSelectedCounselId('');
      setErrors((e) => ({ ...e, selectedAllocationId: '', selectedCounselId: '' }));
      reloadAllocationUsers(supervisorId, selectedStateAttorneyId);
    },
    [reloadAllocationUsers, selectedStateAttorneyId],
  );

  const handleStateAttorneyChange = useCallback(
    (stateAttorneyId: string) => {
      setSelectedStateAttorneyId(stateAttorneyId);
      setSelectedCounselId('');
      setErrors((e) => ({ ...e, selectedStateAttorneyId: '', selectedCounselId: '' }));
      reloadAllocationUsers(selectedSupervisorId, stateAttorneyId);
    },
    [reloadAllocationUsers, selectedSupervisorId],
  );

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!selectedCounselId) {
      next.selectedCounselId = t('tasks.assign.counselRequired');
    }
    if (!workflowInstanceStageUserTaskId?.trim()) {
      next.workflowInstanceStageUserTaskId = t('tasks.assign.userTaskIdRequired');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const counsel = allocationUsers.find((u) => u.caseRequestCategoryAllocationUserId === selectedCounselId) ?? null;
    if (!counsel) return;

    await onSave({
      supervisorId: selectedSupervisorId || undefined,
      stateAttorneyId: selectedStateAttorneyId || undefined,
      caseRequestCategoryAllocationUserId: counsel.caseRequestCategoryAllocationUserId,
      userId: counsel.userId,
    });
  };

  const canSave =
    !saving &&
    Boolean(selectedCounselId) &&
    Boolean(workflowInstanceStageUserTaskId?.trim());

  return (
    <OverlayModal
      isOpen={isOpen}
      overlayClassName="overlay"
      contentClassName="cn-mbox cfg-assignment-modal"
      centerVertically
      onClose={onClose}
    >
      <div className="cn-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span>{t('tasks.assign.configureAssignment')}</span>
        <button type="button" className="cn-x" aria-label={t('common.close')} onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="cn-body">
        <div className="cfg-assignment-form">
          <div className="ccr-fg">
            <label>
              {t('tasks.assign.counsel')} <span className="req">*</span>
            </label>
            <WizardSelect
              id={`${idPrefix}-counsel`}
              value={selectedCounselId}
              placeholder={counselPlaceholder}
              options={counselSelectOptions}
              disabled={!usersReady || usersState === 'error'}
              loading={usersState === 'loading'}
              hasError={Boolean(errors.selectedCounselId)}
              emptyMessage={t('tasks.assign.noUsersFound')}
              searchable
              onChange={(id) => {
                setSelectedCounselId(id);
                setErrors((e) => ({ ...e, selectedCounselId: '' }));
              }}
            />
            {errors.selectedCounselId ? (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
                {errors.selectedCounselId}
              </div>
            ) : null}
          </div>

          <div className="ccr-fg">
            <label>{t('admin.shared.supervisor')}</label>
            <WizardSelect
              id={`${idPrefix}-supervisor`}
              value={selectedSupervisorId}
              placeholder={supervisorPlaceholder}
              options={supervisorSelectOptions}
              disabled={allocationsState === 'error'}
              loading={allocationsState === 'loading'}
              hasError={Boolean(errors.selectedAllocationId)}
              emptyMessage={t('common.noDataFound')}
              searchable
              onChange={handleSupervisorChange}
            />
            {errors.selectedAllocationId ? (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
                {errors.selectedAllocationId}
              </div>
            ) : null}
          </div>

          <div className="ccr-fg">
            <label>{t('tasks.assign.stateAttorney')}</label>
            <WizardSelect
              id={`${idPrefix}-state-attorney`}
              value={selectedStateAttorneyId}
              placeholder={stateAttorneyPlaceholder}
              options={stateAttorneySelectOptions}
              disabled={allocationsState === 'error'}
              loading={allocationsState === 'loading'}
              hasError={Boolean(errors.selectedStateAttorneyId)}
              emptyMessage={t('common.noDataFound')}
              searchable
              onChange={handleStateAttorneyChange}
            />
            {errors.selectedStateAttorneyId ? (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
                {errors.selectedStateAttorneyId}
              </div>
            ) : null}
            {errors.workflowInstanceStageUserTaskId ? (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
                {errors.workflowInstanceStageUserTaskId}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="cn-footer">
        <button type="button" className="mfb" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="mfb mfb-dk"
          onClick={() => void handleSave()}
          disabled={!canSave}
        >
          {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </OverlayModal>
  );
}
